import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bookmark,
  Check,
  CheckCircle,
  CircleOff,
  CornerDownRight,
  ExternalLink,
  GripVertical,
  Highlighter,
  LoaderCircle,
  Megaphone,
  MessageCircleReply,
  MessageSquareText,
  MonitorX,
  MonitorUp,
  Radio,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  ShieldCheck,
  ShoppingBag,
  Star,
  Trash2,
  UserRoundCheck,
  Users,
  Wifi,
  WifiOff,
  Wrench,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { errorMessage } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import {
  coverageStateSchema,
  eventsSchema,
  messageThreadSchema,
  messageUpdateSchema,
  moderationMessageSchema,
  pinQueueSchema,
} from '../../lib/schemas';
import { getSocket } from '../../lib/socket';
import { clearLiveMessageCache, mergeLiveMessage, mergeLiveMessages, structurallyShareLiveMessages } from '../../lib/liveMessages';
import { isTeamSender } from '../../lib/messageIdentity';
import type {
  AuthenticatedUser,
  BuyerFeed,
  CoverageState,
  EventSummary,
  ModerationMessage,
  PinQueueItem,
  SalesBuyer,
  SavedSnippet,
} from '../../types';
import { getEvents, toggleEvent } from './eventsApi';
import { ModulePage } from './ModulePage';
import {
  addBuyerMention,
  announce,
  answerPin,
  clearTvShoutouts,
  deleteMessage,
  getAnsweredMessages,
  getBuyerFeed,
  getModerationMessages,
  getModerationMessagesAfter,
  getModerationRuntime,
  getPinQueue,
  getSnippets,
  markMessageAnswered,
  pinMessage,
  recordSnippetUse,
  removeBuyerMention,
  removeSnippet,
  reopenMessage,
  resetChat,
  replyToMessage,
  saveSnippet,
  sendTvMessage,
  toggleHighlight,
} from './moderationApi';
import { getTeamCoverage, joinRoom, leaveRoom } from './teamApi';

type FeedMode = 'all' | 'questions' | 'highlighted' | 'vip' | 'onair' | 'answered';
type MessageAction = 'highlight' | 'pin' | 'answer' | 'reopen' | 'delete';
type ToolsTab = 'sales' | 'team' | 'broadcast';
type ConfirmState = { kind: 'delete'; message: ModerationMessage } | { kind: 'tv' } | { kind: 'reset' };

interface ModerationConsoleProps {
  user: AuthenticatedUser;
}

interface StatusMessage {
  tone: 'ok' | 'error';
  text: string;
}

const feedModes: Array<{ id: FeedMode; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'questions', label: 'Questions' },
  { id: 'highlighted', label: 'Highlighted' },
  { id: 'vip', label: 'VIP' },
  { id: 'onair', label: 'On Air' },
  { id: 'answered', label: 'Answered' },
];

const DEFAULT_TOOLS_WIDTH = 27;
const MIN_TOOLS_WIDTH = 24;
const MAX_TOOLS_WIDTH = 40;
const TOOLS_WIDTH_STORAGE_KEY = 'modiq_moderation_tools_width';

export function ModerationConsole({ user }: ModerationConsoleProps) {
  const queryClient = useQueryClient();
  const layoutRef = useRef<HTMLDivElement>(null);
  const lastToolsResizePress = useRef(0);
  const [selectedRooms, setSelectedRooms] = useState<Set<number>>(new Set());
  const [feedMode, setFeedMode] = useState<FeedMode>('all');
  const [replyTargetId, setReplyTargetId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [privateReply, setPrivateReply] = useState(true);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [buyerLimit, setBuyerLimit] = useState(30);
  const [toolsTab, setToolsTab] = useState<ToolsTab>('sales');
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [toolsWidth, setToolsWidth] = useState(readToolsWidth);
  const [resizingTools, setResizingTools] = useState(false);
  const [socketConnected, setSocketConnected] = useState(() => getSocket().connected);

  const messages = useQuery<ModerationMessage[]>({ queryKey: queryKeys.moderationMessages, queryFn: ({ signal }) => getModerationMessages(signal), refetchInterval: 30_000, structuralSharing: structurallyShareLiveMessages });
  const answered = useQuery({ queryKey: queryKeys.moderationAnswered, queryFn: ({ signal }) => getAnsweredMessages(signal), refetchInterval: 60_000 });
  const pins = useQuery({ queryKey: queryKeys.moderationPins, queryFn: ({ signal }) => getPinQueue(signal), refetchInterval: 30_000 });
  const runtime = useQuery({ queryKey: queryKeys.moderationRuntime, queryFn: ({ signal }) => getModerationRuntime(signal), staleTime: Infinity });
  const events = useQuery({ queryKey: queryKeys.events, queryFn: ({ signal }) => getEvents(signal), refetchInterval: 30_000 });
  const coverage = useQuery({ queryKey: queryKeys.teamCoverage, queryFn: ({ signal }) => getTeamCoverage(signal), refetchInterval: 30_000 });
  const snippets = useQuery({ queryKey: queryKeys.moderationSnippets, queryFn: ({ signal }) => getSnippets(signal), refetchInterval: 60_000 });
  const buyers = useQuery({
    queryKey: queryKeys.moderationBuyers(buyerLimit),
    queryFn: ({ signal }) => getBuyerFeed(buyerLimit, signal),
    refetchInterval: 10_000,
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(TOOLS_WIDTH_STORAGE_KEY, String(toolsWidth));
    } catch {
      // Local preferences are optional when browser storage is unavailable.
    }
  }, [toolsWidth]);

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.moderationMessages }),
      queryClient.invalidateQueries({ queryKey: queryKeys.moderationAnswered }),
      queryClient.invalidateQueries({ queryKey: queryKeys.moderationPins }),
      queryClient.invalidateQueries({ queryKey: queryKeys.events }),
      queryClient.invalidateQueries({ queryKey: queryKeys.teamCoverage }),
      queryClient.invalidateQueries({ queryKey: queryKeys.moderationSnippets }),
      queryClient.invalidateQueries({ queryKey: ['moderation', 'buyers'] }),
    ]);
  }, [queryClient]);

  const clearChatView = useCallback(async () => {
    setSelectedRooms(new Set());
    setFeedMode('all');
    setReplyTargetId(null);
    setReplyText('');
    await clearLiveMessageCache(queryClient);
  }, [queryClient]);

  const catchUpMessages = useCallback(async () => {
    const current = queryClient.getQueryData<ModerationMessage[]>(queryKeys.moderationMessages) ?? [];
    let afterId = current.reduce((highest, message) => Math.max(highest, message.id), 0);
    if (!afterId) return;
    for (let page = 0; page < 20; page += 1) {
      const incoming = await getModerationMessagesAfter(afterId);
      if (!incoming.length) return;
      queryClient.setQueryData<ModerationMessage[]>(
        queryKeys.moderationMessages,
        cached => mergeLiveMessages(cached, incoming),
      );
      const nextAfterId = incoming.reduce((highest, message) => Math.max(highest, message.id), afterId);
      if (nextAfterId === afterId || incoming.length < 500) return;
      afterId = nextAfterId;
    }
  }, [queryClient]);

  useEffect(() => {
    const socket = getSocket();
    const reload = () => {
      setSocketConnected(true);
      void catchUpMessages().catch(() => {}).then(refresh);
      socket.emit('request_coverage');
    };
    const disconnected = () => setSocketConnected(false);
    const newMessage = (payload: ModerationMessage) => {
      const parsed = moderationMessageSchema.safeParse(payload);
      if (!parsed.success) return;
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationMessages, current => mergeLiveMessage(current, parsed.data));
    };
    const updateMessage = (payload: unknown) => {
      const parsed = messageUpdateSchema.safeParse(payload);
      if (!parsed.success) return;
      const apply = (current: ModerationMessage[] | undefined): ModerationMessage[] => (current ?? []).map(message => {
        if (message.id !== parsed.data.id) return message;
        const next: ModerationMessage = {
          ...message,
          ...(parsed.data.flagged !== undefined ? { flagged: parsed.data.flagged } : {}),
          ...(parsed.data.highlighted !== undefined ? { highlighted: parsed.data.highlighted } : {}),
          ...(parsed.data.pinned !== undefined ? { pinned: parsed.data.pinned } : {}),
          ...(parsed.data.answered !== undefined ? { answered: parsed.data.answered } : {}),
          ...(parsed.data.deleted !== undefined ? { deleted: parsed.data.deleted } : {}),
          ...(parsed.data.reply !== undefined ? { reply: parsed.data.reply } : {}),
        };
        if (parsed.data.reply && !message.thread.some(entry => entry.who === 'mod' && entry.text === parsed.data.reply)) {
          next.thread = [...message.thread, { who: 'mod', name: user.name || user.username, text: parsed.data.reply }];
        }
        return next;
      });
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationMessages, apply);
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationAnswered, apply);
      if (parsed.data.answered !== undefined) void queryClient.invalidateQueries({ queryKey: queryKeys.moderationAnswered });
    };
    const threadMessage = (payload: unknown) => {
      const parsed = messageThreadSchema.safeParse(payload);
      if (!parsed.success) return;
      const apply = (current: ModerationMessage[] | undefined) => (current ?? []).map(message => message.id === parsed.data.id
        ? { ...message, answered: true, thread: [...message.thread, { who: parsed.data.who, name: parsed.data.name, text: parsed.data.text }] }
        : message);
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationMessages, apply);
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationAnswered, apply);
    };
    const deleteFromFeed = (payload: unknown) => {
      const parsed = messageUpdateSchema.pick({ id: true }).safeParse(payload);
      if (!parsed.success) return;
      const apply = (current: ModerationMessage[] | undefined) => (current ?? []).map(message => message.id === parsed.data.id ? { ...message, deleted: true } : message);
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationMessages, apply);
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationAnswered, apply);
    };
    const resetMessages = () => { void clearChatView(); };
    const updatePins = (payload: PinQueueItem[]) => {
      const parsed = pinQueueSchema.safeParse(payload);
      if (!parsed.success) return;
      queryClient.setQueryData(queryKeys.moderationPins, parsed.data);
      const pinnedIds = new Set(parsed.data.map(pin => pin.message_id));
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationMessages, current => (current ?? []).map(message => ({ ...message, pinned: pinnedIds.has(message.id) })));
    };
    const updateEvents = (payload: EventSummary[]) => {
      const parsed = eventsSchema.safeParse(payload);
      if (parsed.success) queryClient.setQueryData(queryKeys.events, parsed.data);
    };
    const updateCoverage = (payload: CoverageState) => {
      const parsed = coverageStateSchema.safeParse(payload);
      if (parsed.success) queryClient.setQueryData(queryKeys.teamCoverage, parsed.data);
    };
    const removeRoom = (payload: unknown) => {
      if (!payload || typeof payload !== 'object' || !('meeting_id' in payload)) return;
      const meetingId = String(payload.meeting_id ?? '');
      queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationMessages, current => (current ?? []).map(message => message.meeting_id === meetingId ? { ...message, deleted: true } : message));
    };
    const reloadSnippets = () => { void queryClient.invalidateQueries({ queryKey: queryKeys.moderationSnippets }); };
    const reloadBuyers = () => { void queryClient.invalidateQueries({ queryKey: ['moderation', 'buyers'] }); };

    socket.on('connect', reload);
    socket.on('disconnect', disconnected);
    socket.on('new_message', newMessage);
    socket.on('message_updated', updateMessage);
    socket.on('message_thread', threadMessage);
    socket.on('message_deleted', deleteFromFeed);
    socket.on('messages_reset', resetMessages);
    socket.on('pin_update', updatePins);
    socket.on('events_update', updateEvents);
    socket.on('coverage_update', updateCoverage);
    socket.on('room_removed', removeRoom);
    socket.on('snippets_update', reloadSnippets);
    socket.on('mentions_update', reloadBuyers);
    socket.emit('request_coverage');
    return () => {
      socket.off('connect', reload);
      socket.off('disconnect', disconnected);
      socket.off('new_message', newMessage);
      socket.off('message_updated', updateMessage);
      socket.off('message_thread', threadMessage);
      socket.off('message_deleted', deleteFromFeed);
      socket.off('messages_reset', resetMessages);
      socket.off('pin_update', updatePins);
      socket.off('events_update', updateEvents);
      socket.off('coverage_update', updateCoverage);
      socket.off('room_removed', removeRoom);
      socket.off('snippets_update', reloadSnippets);
      socket.off('mentions_update', reloadBuyers);
    };
  }, [catchUpMessages, clearChatView, queryClient, refresh, user.name, user.username]);

  const visibleEvents = useMemo(() => (events.data ?? []).filter(event => event.active || runtime.data?.localDemo), [events.data, runtime.data?.localDemo]);
  const eventByMeeting = useMemo(() => new Map((events.data ?? []).map(event => [`${event.zoom_account}:${event.zoom_id}`, event])), [events.data]);
  const pinIds = useMemo(() => new Set((pins.data ?? []).map(pin => pin.message_id)), [pins.data]);
  const pinPositions = useMemo(() => new Map((pins.data ?? []).map((pin, index) => [pin.message_id, index + 1])), [pins.data]);
  const allMessages = useMemo(() => mergeMessages(messages.data ?? [], answered.data ?? [], pinIds), [answered.data, messages.data, pinIds]);
  const roomMatches = (message: ModerationMessage) => {
    if (selectedRooms.size === 0) return true;
    const event = eventForMessage(message, eventByMeeting);
    return event ? selectedRooms.has(event.id) : false;
  };
  const scopedMessages = allMessages.filter(message => !message.deleted && roomMatches(message));
  const openFeed = scopedMessages.filter(message => !message.answered);
  const answeredFeed = scopedMessages.filter(message => message.answered);
  const filterCounts: Record<FeedMode, number> = {
    all: openFeed.length,
    questions: openFeed.filter(message => message.message.includes('?')).length,
    highlighted: openFeed.filter(message => message.highlighted).length,
    vip: openFeed.filter(message => Boolean(eventForMessage(message, eventByMeeting)?.is_vip)).length,
    onair: openFeed.filter(message => message.pinned).length,
    answered: answeredFeed.length,
  };
  const visibleFeed = (feedMode === 'answered' ? answeredFeed : openFeed.filter(message => feedMode === 'all'
    || (feedMode === 'questions' && message.message.includes('?'))
    || (feedMode === 'highlighted' && message.highlighted)
    || (feedMode === 'onair' && message.pinned)
    || (feedMode === 'vip' && Boolean(eventForMessage(message, eventByMeeting)?.is_vip))))
    .sort(newestFirst)
    .slice(0, 300);
  const replyTarget = allMessages.find(message => message.id === replyTargetId) ?? null;
  const replyTargetEvent = replyTarget ? eventForMessage(replyTarget, eventByMeeting) : undefined;
  const receivedCount = allMessages.filter(message => !message.deleted).length;
  const openCount = allMessages.filter(message => !message.deleted && !message.answered).length;
  const highlightedCount = allMessages.filter(message => !message.deleted && message.highlighted).length;
  const isFetching = [messages, answered, pins, events, coverage].some(query => query.isFetching);
  const feedError = [messages, answered, pins, events].find(query => query.isError)?.error;
  const isFeedPending = messages.isPending || answered.isPending || pins.isPending || events.isPending;

  const messageAction = useMutation({
    mutationFn: ({ kind, id }: { kind: MessageAction; id: number }) => {
      if (kind === 'highlight') return toggleHighlight(id);
      if (kind === 'pin') return pinMessage(id);
      if (kind === 'answer') return markMessageAnswered(id);
      if (kind === 'reopen') return reopenMessage(id);
      return deleteMessage(id);
    },
    onSuccess: async (_result, action) => {
      setStatus({ tone: 'ok', text: action.kind === 'pin' ? 'Added to On Air.' : action.kind === 'reopen' ? 'Message returned to the live queue.' : 'Message updated.' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.moderationMessages }),
        queryClient.invalidateQueries({ queryKey: queryKeys.moderationAnswered }),
        queryClient.invalidateQueries({ queryKey: queryKeys.moderationPins }),
      ]);
    },
    onError: error => setStatus({ tone: 'error', text: errorMessage(error, 'The message could not be changed.') }),
  });
  const reply = useMutation({
    mutationFn: ({ id, text, privateReply: privateValue }: { id: number; text: string; privateReply: boolean }) => replyToMessage(id, text, privateValue),
    onSuccess: async result => {
      const text = !result.zoomed ? 'Reply saved. No live bot was available.' : result.to === 'private' ? 'Reply sent privately.' : 'Reply sent to everyone.';
      setStatus({ tone: result.zoomed ? 'ok' : 'error', text });
      setReplyTargetId(null);
      setReplyText('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.moderationMessages }),
        queryClient.invalidateQueries({ queryKey: queryKeys.moderationAnswered }),
      ]);
    },
    onError: error => setStatus({ tone: 'error', text: errorMessage(error, 'The reply could not be sent.') }),
  });
  const nextPin = useMutation({
    mutationFn: answerPin,
    onSuccess: result => {
      queryClient.setQueryData(queryKeys.moderationPins, result.queue);
      setStatus({ tone: 'ok', text: result.queue.length ? 'Advanced to the next On Air message.' : 'On Air queue cleared.' });
    },
    onError: error => setStatus({ tone: 'error', text: errorMessage(error, 'The On Air queue could not advance.') }),
  });
  const clearShoutouts = useMutation({
    mutationFn: clearTvShoutouts,
    onSuccess: async () => {
      setStatus({ tone: 'ok', text: 'TV shoutouts cleared.' });
      await queryClient.invalidateQueries({ queryKey: ['moderation', 'buyers'] });
    },
    onError: error => setStatus({ tone: 'error', text: errorMessage(error, 'The TV shoutouts could not be cleared.') }),
  });
  const chatReset = useMutation({
    mutationFn: resetChat,
    onSuccess: async () => {
      await clearChatView();
      setStatus({ tone: 'ok', text: 'Chat view reset. Saved history remains available in Data.' });
    },
    onError: error => setStatus({ tone: 'error', text: errorMessage(error, 'The chat view could not be reset.') }),
  });

  function selectReply(message: ModerationMessage) {
    setReplyTargetId(message.id);
    const webinar = eventForMessage(message, eventByMeeting)?.event_type === 'webinar';
    setPrivateReply(!webinar);
  }

  function requestDelete(message: ModerationMessage) {
    setConfirm({ kind: 'delete', message });
  }

  function acceptConfirm() {
    if (!confirm) return;
    if (confirm.kind === 'delete') messageAction.mutate({ kind: 'delete', id: confirm.message.id });
    else if (confirm.kind === 'tv') clearShoutouts.mutate();
    else chatReset.mutate();
    setConfirm(null);
  }

  function submitReply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!replyTarget || !replyText.trim()) return;
    reply.mutate({ id: replyTarget.id, text: replyText.trim(), privateReply });
  }

  function toggleRoom(eventId: number) {
    setSelectedRooms(current => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId); else next.add(eventId);
      return next;
    });
  }

  function resizeToolsFromPointer(clientX: number) {
    const bounds = layoutRef.current?.getBoundingClientRect();
    if (!bounds?.width) return;
    const next = ((bounds.right - clientX) / bounds.width) * 100;
    setToolsWidth(clampToolsWidth(Math.round(next * 10) / 10));
  }

  function startToolsResize(event: ReactPointerEvent<HTMLDivElement>) {
    const pressedAt = Date.now();
    const repeatedPress = pressedAt - lastToolsResizePress.current < 450;
    lastToolsResizePress.current = pressedAt;
    if (repeatedPress) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      setResizingTools(false);
      setToolsWidth(DEFAULT_TOOLS_WIDTH);
      return;
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setResizingTools(true);
    resizeToolsFromPointer(event.clientX);
  }

  function moveToolsResize(event: ReactPointerEvent<HTMLDivElement>) {
    if (!resizingTools && !event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    resizeToolsFromPointer(event.clientX);
  }

  function stopToolsResize(event: ReactPointerEvent<HTMLDivElement>) {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setResizingTools(false);
  }

  function resizeToolsWithKeyboard(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home') return;
    event.preventDefault();
    if (event.key === 'Home') setToolsWidth(DEFAULT_TOOLS_WIDTH);
    else setToolsWidth(width => clampToolsWidth(width + (event.key === 'ArrowLeft' ? 1 : -1)));
  }

  const emptyState = emptyStateFor(feedMode);
  const layoutStyle = { '--moderation-tools-width': `${toolsWidth}%` } as CSSProperties;

  return (
    <ModulePage
      id="moderation-title"
      title="Moderation"
      className="moderation-console"
      innerClassName="moderation-console__inner"
      bodyClassName="moderation-console__body"
      flush
      meta={<div className="moderation-statuses"><span className={`moderation-live ${visibleEvents.some(event => event.active) ? '' : 'idle'}`}><i />{visibleEvents.some(event => event.active) ? 'Live' : 'Standing by'}</span><span className={`moderation-connection ${socketConnected ? '' : 'reconnecting'}`} title={socketConnected ? 'Live message connection is synced' : 'Live message connection is reconnecting'}>{socketConnected ? <Wifi size={14} /> : <WifiOff size={14} />}{socketConnected ? 'Synced' : 'Reconnecting'}</span></div>}
      actions={(
        <div className="moderation-header__metrics" aria-label="Session metrics">
          <span title="Every message received in the current session"><strong>{receivedCount}</strong> received</span>
          <span title="Messages still waiting for an answer"><strong>{openCount}</strong> open</span>
          <span title="Messages highlighted by the moderation team"><strong>{highlightedCount}</strong> highlighted</span>
          <button className="secondary-button compact moderation-reset-button" type="button" disabled={chatReset.isPending} onClick={() => setConfirm({ kind: 'reset' })}><RotateCcw size={15} />Reset chat</button>
          <button className="icon-button" type="button" title="Refresh moderation" aria-label="Refresh moderation" disabled={isFetching} onClick={() => void refresh()}><RefreshCw className={isFetching ? 'spin' : ''} size={16} /></button>
        </div>
      )}
    >
      {status ? <div className={`moderation-feedback ${status.tone}`} role={status.tone === 'error' ? 'alert' : 'status'}>{status.tone === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}<span>{status.text}</span><button className="icon-button" type="button" title="Dismiss status" aria-label="Dismiss status" onClick={() => setStatus(null)}><X size={15} /></button></div> : null}

      <div className={`moderation-layout ${resizingTools ? 'resizing' : ''}`} ref={layoutRef} style={layoutStyle}>
          <RoomRail
            events={visibleEvents}
            messages={allMessages}
            selected={selectedRooms}
            onToggle={toggleRoom}
            onAll={() => setSelectedRooms(new Set())}
          />

          <main className="moderation-feed">
            <div className="moderation-feed__toolbar">
              <div className="segmented-control moderation-segments" role="group" aria-label="Message filter">
                {feedModes.map(mode => <button className={feedMode === mode.id ? 'active' : ''} type="button" aria-pressed={feedMode === mode.id} key={mode.id} onClick={() => setFeedMode(mode.id)}>{mode.label}<span>{filterCounts[mode.id]}</span></button>)}
              </div>
              <span>{visibleFeed.length} shown · newest first</span>
            </div>

            <div className="moderation-feed__scroll">
              {feedError ? <div className="moderation-feed__state error" role="alert"><span className="moderation-state__icon"><AlertCircle size={24} /></span><strong>Moderation could not be loaded</strong><p>Your rooms are still covered and nothing was lost. {errorMessage(feedError, 'Try refreshing the console.')}</p><button className="primary-button" type="button" onClick={() => void refresh()}>Try again</button></div> : null}
              {!feedError && isFeedPending ? <ModerationSkeletons /> : null}
              {!feedError && !isFeedPending && visibleFeed.length === 0 ? <div className="moderation-feed__state"><span className="moderation-state__icon"><MessageSquareText size={24} /></span><strong>{emptyState.title}</strong><p>{emptyState.body}</p>{selectedRooms.size || feedMode !== 'all' ? <button className="secondary-button" type="button" onClick={() => { setSelectedRooms(new Set()); setFeedMode('all'); }}>Show all rooms and messages</button> : null}</div> : null}
              <div className="moderation-message-list">
                {!feedError && !isFeedPending ? visibleFeed.map(message => (
                  <MessageCard
                    event={eventForMessage(message, eventByMeeting)}
                    key={message.id}
                    message={message}
                    queuePosition={pinPositions.get(message.id) ?? null}
                    pending={messageAction.isPending && messageAction.variables.id === message.id}
                    selected={replyTargetId === message.id}
                    onReply={() => selectReply(message)}
                    onHighlight={() => messageAction.mutate({ kind: 'highlight', id: message.id })}
                    onPin={() => messageAction.mutate({ kind: 'pin', id: message.id })}
                    onAnswer={() => messageAction.mutate({ kind: 'answer', id: message.id })}
                    onReopen={() => messageAction.mutate({ kind: 'reopen', id: message.id })}
                    onDelete={() => requestDelete(message)}
                  />
                )) : null}
              </div>
            </div>

            <form className={`moderation-reply ${replyTarget ? 'active' : ''}`} onSubmit={submitReply}>
              <div className="moderation-reply__target">
                <MessageCircleReply size={16} />
                <span>{replyTarget ? `Replying to ${replyTarget.sender_name} · ${replyTargetEvent?.room_name || replyTargetEvent?.topic || replyTarget.room_name || 'Unknown room'}` : 'Select Reply on a message to answer it'}</span>
                {replyTarget ? <button className="icon-button" type="button" title="Cancel reply" aria-label="Cancel reply" onClick={() => setReplyTargetId(null)}><X size={15} />Cancel</button> : null}
              </div>
              <div className="moderation-reply__row">
                <textarea maxLength={4096} rows={1} disabled={!replyTarget || reply.isPending} placeholder={replyTarget ? 'Type the response...' : 'No message selected'} value={replyText} onChange={event => setReplyText(event.target.value)} />
                <button className="primary-button" type="submit" disabled={!replyTarget || !replyText.trim() || reply.isPending}>{reply.isPending ? <LoaderCircle className="spin" size={16} /> : <Send size={16} />}{reply.isPending ? 'Sending' : 'Send'}</button>
              </div>
              {replyTarget ? <label className="moderation-reply__private"><input type="checkbox" checked={privateReply} disabled={replyTargetEvent?.event_type === 'webinar'} onChange={event => setPrivateReply(event.target.checked)} /><span>{replyTargetEvent?.event_type === 'webinar' ? 'Webinar replies go to everyone.' : 'Sent as a direct message from your room bot.'}</span></label> : null}
            </form>
          </main>

          <aside className="moderation-sidebar" aria-label="Moderation tools">
            <div
              className="moderation-tools__resize"
              role="separator"
              aria-label="Resize moderation tools"
              aria-orientation="vertical"
              aria-valuemin={MIN_TOOLS_WIDTH}
              aria-valuemax={MAX_TOOLS_WIDTH}
              aria-valuenow={Math.round(toolsWidth)}
              aria-valuetext={`${toolsWidth}% width`}
              tabIndex={0}
              title="Drag to resize tools. Double-click to reset."
              onDoubleClick={() => setToolsWidth(DEFAULT_TOOLS_WIDTH)}
              onKeyDown={resizeToolsWithKeyboard}
              onPointerDown={startToolsResize}
              onPointerMove={moveToolsResize}
              onPointerUp={stopToolsResize}
              onPointerCancel={stopToolsResize}
              onLostPointerCapture={() => setResizingTools(false)}
            ><GripVertical size={14} /></div>
            <header className="moderation-tools__header"><Wrench size={16} /><h2>Tools</h2></header>
            <OnAirPanel pins={pins.data ?? []} pending={nextPin.isPending} onNext={pin => nextPin.mutate(pin.pin_id)} />
            <div className="moderation-tools__tabs" role="tablist" aria-label="Moderation tools">
              <button className={toolsTab === 'sales' ? 'active' : ''} type="button" role="tab" aria-selected={toolsTab === 'sales'} onClick={() => setToolsTab('sales')}><ShoppingBag size={15} />Sales</button>
              <button className={toolsTab === 'team' ? 'active' : ''} type="button" role="tab" aria-selected={toolsTab === 'team'} onClick={() => setToolsTab('team')}><Users size={15} />Team</button>
              <button className={toolsTab === 'broadcast' ? 'active' : ''} type="button" role="tab" aria-selected={toolsTab === 'broadcast'} onClick={() => setToolsTab('broadcast')}><Megaphone size={15} />Broadcast</button>
            </div>
            <div className="moderation-tools__body">
              {toolsTab === 'sales' ? <BuyerPanel feed={buyers.data} loading={buyers.isPending} clearing={clearShoutouts.isPending} limit={buyerLimit} onLimit={setBuyerLimit} onRequestClear={() => setConfirm({ kind: 'tv' })} onChanged={() => void queryClient.invalidateQueries({ queryKey: ['moderation', 'buyers'] })} onStatus={setStatus} /> : null}
              {toolsTab === 'team' ? <CoveragePanel user={user} state={coverage.data} events={events.data ?? []} onStatus={setStatus} onChanged={() => void queryClient.invalidateQueries({ queryKey: queryKeys.teamCoverage })} /> : null}
              {toolsTab === 'broadcast' ? <AnnouncementPanel state={coverage.data} snippets={snippets.data?.snippets ?? []} snippetOwner={snippets.data?.me ?? null} isAdmin={snippets.data?.isAdmin ?? false} onStatus={setStatus} onChanged={() => void queryClient.invalidateQueries({ queryKey: queryKeys.moderationSnippets })} /> : null}
            </div>
          </aside>
      </div>
      {confirm ? <ConfirmDialog state={confirm} pending={messageAction.isPending || clearShoutouts.isPending || chatReset.isPending} onCancel={() => setConfirm(null)} onConfirm={acceptConfirm} /> : null}
    </ModulePage>
  );
}

function RoomRail({ events, messages, selected, onToggle, onAll }: { events: EventSummary[]; messages: ModerationMessage[]; selected: Set<number>; onToggle: (id: number) => void; onAll: () => void }) {
  return (
    <nav className="moderation-rooms" aria-label="Filter by room">
      <button className={selected.size === 0 ? 'active' : ''} type="button" title="All rooms" onClick={onAll}>
        <span className="moderation-room__name">All rooms</span>
        <span className="moderation-room__count"><span className="moderation-room__dot all" />{messages.filter(message => !message.deleted && !message.answered).length}</span>
      </button>
      {events.map(event => {
        const count = messages.filter(message => !message.deleted && !message.answered && eventForMessage(message, new Map([[`${event.zoom_account}:${event.zoom_id}`, event]]))?.id === event.id).length;
        const label = event.room_name || event.topic || `Room ${event.id}`;
        return <button className={selected.has(event.id) ? 'active' : ''} type="button" title={label} aria-pressed={selected.has(event.id)} key={event.id} onClick={() => onToggle(event.id)}>
          <span className="moderation-room__name">{label}{event.is_vip ? <Star size={10} /> : null}</span>
          <span className="moderation-room__count"><span className="moderation-room__dot" style={{ background: event.color || '#6b7280' }} />{count}</span>
        </button>;
      })}
    </nav>
  );
}

function MessageCard({ message, event, queuePosition, pending, selected, onReply, onHighlight, onPin, onAnswer, onReopen, onDelete }: { message: ModerationMessage; event: EventSummary | undefined; queuePosition: number | null; pending: boolean; selected: boolean; onReply: () => void; onHighlight: () => void; onPin: () => void; onAnswer: () => void; onReopen: () => void; onDelete: () => void }) {
  const teamSender = isTeamSender(message);
  const roomName = event?.room_name || event?.topic || message.room_name || 'Unknown room';
  return (
    <article className={`moderation-message ${message.highlighted ? 'highlighted' : ''} ${queuePosition ? 'on-air' : ''} ${event?.is_vip ? 'vip' : ''} ${message.answered ? 'answered' : ''} ${selected ? 'selected' : ''}`}>
      <span className="moderation-message__spine" aria-hidden="true" />
      <div className="moderation-message__body">
        <header>
          <span className={`moderation-avatar ${teamSender ? 'team' : ''}`} aria-label={teamSender ? 'UgenticAI team message' : undefined} title={teamSender ? 'Team message' : message.sender_name}>{teamSender ? <ShieldCheck size={17} /> : initials(message.sender_name)}</span>
          <div className="moderation-message__identity">
            <div className="moderation-message__name-row">
              <div className="moderation-message__sender-details">
                <span><strong>{message.sender_name}</strong>{teamSender ? <b className="team"><ShieldCheck size={12} />Team</b> : null}{event?.is_vip ? <b className="vip"><Star size={11} />VIP</b> : null}{queuePosition ? <b className="on-air"><Radio size={11} />{queuePosition === 1 ? 'On air now' : `On air · ${queuePosition}`}</b> : null}{message.highlighted ? <b className="highlighted"><Highlighter size={11} />Highlighted</b> : null}</span>
                <div className="moderation-message__meta"><span>{teamSender ? 'Team' : message.email || 'Attendee'}</span></div>
              </div>
              <div className="moderation-message__time-room">
                <time dateTime={new Date(message.timestamp).toISOString()} title={fullTimestamp(message.timestamp)}>{messageTimestamp(message.timestamp)}</time>
                <span title={roomName}><i style={{ background: event?.color || '#6b7280' }} />{roomName}</span>
              </div>
            </div>
          </div>
          <div className="moderation-message__actions">
            {message.answered ? <button className="secondary-button" type="button" disabled={pending} onClick={onReopen}><RotateCcw size={15} />Reopen</button> : <>
              <button className={`primary-button ${selected ? 'selected' : ''}`} type="button" disabled={pending} onClick={onReply}><MessageCircleReply size={15} />Reply</button>
              <button className={`secondary-button ${message.highlighted ? 'active' : ''}`} type="button" disabled={pending} onClick={onHighlight}><Highlighter size={15} />{message.highlighted ? 'Highlighted' : 'Highlight'}</button>
              <button className={`secondary-button ${queuePosition ? 'on-air' : ''}`} type="button" disabled={pending || Boolean(queuePosition)} title={queuePosition ? `Position ${queuePosition} in the On Air queue` : 'Add to the On Air queue'} onClick={onPin}><Radio size={15} />On Air</button>
              <button className="secondary-button" type="button" disabled={pending} onClick={onAnswer}><Check size={15} />Answered</button>
              <button className="icon-button moderation-message__delete" type="button" disabled={pending} title="Delete message" aria-label={`Delete message from ${message.sender_name}`} onClick={onDelete}><Trash2 size={16} /></button>
            </>}
          </div>
        </header>
        <p>{message.message}</p>
        {message.thread.length ? <div className="moderation-thread">{message.thread.map((entry, index) => <div className={entry.who} key={`${entry.who}-${index}`}><strong>{entry.who === 'mod' ? entry.name || 'Moderator' : entry.name || message.sender_name}</strong><span>{entry.text}</span></div>)}</div> : null}
        {message.answered && message.reply && message.thread.length === 0 ? <div className="moderation-saved-reply"><CornerDownRight size={15} /><div><strong>Sent reply</strong><span>{message.reply}</span></div></div> : null}
      </div>
    </article>
  );
}

function OnAirPanel({ pins, pending, onNext }: { pins: PinQueueItem[]; pending: boolean; onNext: (pin: PinQueueItem) => void }) {
  const current = pins[0];
  return (
    <section className="moderation-tool on-air-panel" aria-labelledby="on-air-title">
      <header><span><Radio size={15} /><h2 id="on-air-title">On Air Now</h2></span><b>{pins.length} queued</b></header>
      {current ? <><div className="on-air-current"><span><i />{current.room_name || 'Room'} · <time dateTime={new Date(current.timestamp).toISOString()}>{messageTimestamp(current.timestamp)}</time></span><strong>{current.sender_name}</strong><p>{current.message}</p></div>{pins.length > 1 ? <ol>{pins.slice(1, 4).map((pin, index) => <li key={pin.pin_id}><b>{index + 2}</b><span>{pin.sender_name} — {pin.message}</span></li>)}</ol> : null}<button className="primary-button full-width" type="button" disabled={pending} onClick={() => onNext(current)}>{pending ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}Answered, show next</button></> : <div className="moderation-tool__empty"><Radio size={19} /><strong>Nothing on the TV</strong><span>Put a message On Air from the feed.</span></div>}
    </section>
  );
}

function CoveragePanel({ user, state, events, onStatus, onChanged }: { user: AuthenticatedUser; state: CoverageState | undefined; events: EventSummary[]; onStatus: (status: StatusMessage) => void; onChanged: () => void }) {
  const [botName, setBotName] = useState(() => window.localStorage.getItem('modiq_bot_name') || user.name || user.username);
  const action = useMutation({
    mutationFn: ({ roomId, joined }: { roomId: number; joined: boolean }) => joined ? leaveRoom(roomId) : joinRoom(roomId, botName.trim()),
    onSuccess: async result => { onStatus({ tone: 'ok', text: result.joined ? 'Your room bot is joining.' : 'Your room bot has left.' }); onChanged(); },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The room bot could not be changed.') }),
  });
  const eventAction = useMutation({
    mutationFn: toggleEvent,
    onSuccess: () => { onStatus({ tone: 'ok', text: 'Room status updated.' }); },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The room could not be changed.') }),
  });
  const mine = new Map((state?.me?.mine ?? []).map(room => [room.event_id, room]));
  const assignments = state?.coverage.filter(item => item.user_id === user.id && item.status === 'active').length ?? 0;
  const online = state?.members.filter(member => member.online).length ?? 0;

  function updateBotName(value: string) {
    setBotName(value);
    window.localStorage.setItem('modiq_bot_name', value);
  }

  return (
    <section className="moderation-tool coverage-panel" aria-labelledby="coverage-title">
      <header><Users size={16} /><h2 id="coverage-title">Team &amp; Rooms</h2><span>{online} online</span></header>
      <label><span>Your bot name</span><input maxLength={100} value={botName} onChange={event => updateBotName(event.target.value)} /></label>
      <div className="coverage-panel__rooms">
        {(state?.rooms ?? []).map(room => {
          const mineRoom = mine.get(room.id);
          const joined = Boolean(mineRoom?.active);
          const event = events.find(item => item.id === room.id);
          return <div key={room.id}><span className={`coverage-status ${joined ? 'active' : ''}`} /><div><strong>{room.name}</strong><small>{joined ? mineRoom?.bot_name || 'In room' : room.active ? 'Not covering' : 'Paused'}</small></div><button className={joined ? 'danger-button' : 'secondary-button'} type="button" disabled={action.isPending || !botName.trim() || (!room.active && !joined)} onClick={() => action.mutate({ roomId: room.id, joined })}>{joined ? 'Leave' : 'Join'}</button>{user.caps.events && event ? <button className="icon-button" type="button" title={event.active ? 'Pause room' : 'Resume room'} aria-label={event.active ? `Pause ${room.name}` : `Resume ${room.name}`} disabled={eventAction.isPending} onClick={() => eventAction.mutate(event.id)}>{event.active ? <CircleOff size={14} /> : <Radio size={14} />}</button> : null}</div>;
        })}
        {state && state.rooms.length === 0 ? <div className="moderation-tool__empty"><span>No rooms are configured.</span></div> : null}
      </div>
      {user.caps.team ? <button className="secondary-button full-width" type="button" onClick={() => { window.location.hash = 'team'; }}><ExternalLink size={14} />Full team board</button> : null}
      <p className="coverage-panel__summary"><UserRoundCheck size={13} />You are covering {assignments} room{assignments === 1 ? '' : 's'}.</p>
    </section>
  );
}

function AnnouncementPanel({ state, snippets, snippetOwner, isAdmin, onStatus, onChanged }: { state: CoverageState | undefined; snippets: SavedSnippet[]; snippetOwner: number | null; isAdmin: boolean; onStatus: (status: StatusMessage) => void; onChanged: () => void }) {
  const [text, setText] = useState('');
  const [label, setLabel] = useState('');
  const [roomIds, setRoomIds] = useState<Set<number>>(new Set());
  const mine = new Set((state?.me?.mine ?? []).filter(room => room.active).map(room => room.event_id));
  const send = useMutation({
    mutationFn: () => announce(text.trim(), [...roomIds]),
    onSuccess: result => {
      const failed = result.total - result.sent;
      onStatus({ tone: failed ? 'error' : 'ok', text: failed ? `Sent to ${result.sent} of ${result.total} rooms.` : `Sent to ${result.sent} room${result.sent === 1 ? '' : 's'}.` });
      if (!failed) setText('');
    },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The room message could not be sent.') }),
  });
  const save = useMutation({
    mutationFn: () => saveSnippet(text.trim(), label.trim()),
    onSuccess: result => { onStatus({ tone: 'ok', text: result.already ? 'That message is already saved.' : 'Saved for the team.' }); setLabel(''); onChanged(); },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The message could not be saved.') }),
  });
  const remove = useMutation({
    mutationFn: removeSnippet,
    onSuccess: () => { onStatus({ tone: 'ok', text: 'Saved message removed.' }); onChanged(); },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The saved message could not be removed.') }),
  });

  function toggleRoom(id: number) {
    setRoomIds(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  function applySnippet(snippet: SavedSnippet) {
    setText(current => `${current}${current ? '\n' : ''}${snippet.body}`);
    void recordSnippetUse(snippet.id);
  }

  return (
    <section className="moderation-tool announcement-panel" aria-labelledby="announcement-title">
      <header><MessageSquareText size={16} /><h2 id="announcement-title">Message a Room</h2></header>
      <div className="announcement-rooms">{(state?.rooms ?? []).map(room => <label className={!mine.has(room.id) ? 'disabled' : ''} key={room.id}><input type="checkbox" disabled={!mine.has(room.id)} checked={roomIds.has(room.id)} onChange={() => toggleRoom(room.id)} /><span>{room.name}</span></label>)}</div>
      <textarea maxLength={2000} rows={4} placeholder="Broadcast to everyone in selected rooms" value={text} onChange={event => setText(event.target.value)} />
      <div className="announcement-actions"><button className="primary-button" type="button" disabled={send.isPending || !text.trim() || roomIds.size === 0} onClick={() => send.mutate()}>{send.isPending ? <LoaderCircle className="spin" size={14} /> : <Send size={14} />}Send</button><button className="secondary-button" type="button" disabled={save.isPending || !text.trim()} onClick={() => save.mutate()}><Save size={14} />Save</button></div>
      <label className="snippet-label"><span>Saved label <small>optional</small></span><input maxLength={40} placeholder="Team shorthand" value={label} onChange={event => setLabel(event.target.value)} /></label>
      <div className="snippet-library"><strong><Bookmark size={13} />Saved library</strong>{snippets.length ? <div>{snippets.map(snippet => <span className="snippet-chip" key={snippet.id}><button type="button" title={snippet.body} onClick={() => applySnippet(snippet)}>{snippet.label}</button>{(snippet.created_by === snippetOwner || isAdmin) ? <button type="button" aria-label={`Remove ${snippet.label}`} title="Remove saved message" disabled={remove.isPending} onClick={() => remove.mutate(snippet.id)}><X size={11} /></button> : null}</span>)}</div> : <small>No saved messages yet.</small>}</div>
    </section>
  );
}

function BuyerPanel({ feed, loading, clearing, limit, onLimit, onRequestClear, onChanged, onStatus }: { feed: BuyerFeed | undefined; loading: boolean; clearing: boolean; limit: number; onLimit: (limit: number) => void; onRequestClear: () => void; onChanged: () => void; onStatus: (status: StatusMessage) => void }) {
  const [tvText, setTvText] = useState('');
  const mention = useMutation({
    mutationFn: ({ buyer, selected }: { buyer: SalesBuyer; selected: boolean }) => selected ? removeBuyerMention(buyer.id) : addBuyerMention(buyer),
    onSuccess: () => { onChanged(); },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The TV shoutout could not be changed.') }),
  });
  const sendMessage = useMutation({
    mutationFn: sendTvMessage,
    onSuccess: () => { setTvText(''); onStatus({ tone: 'ok', text: 'Message sent to the TV shoutouts.' }); onChanged(); },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The TV message could not be sent.') }),
  });
  if (!loading && !feed?.configured) return null;
  return (
    <section className="moderation-tool buyers-panel" aria-labelledby="buyers-title">
      <header><MonitorUp size={16} /><h2 id="buyers-title">Whop · Just bought</h2>{feed ? <span>{feed.mentioned.length} on TV · {feed.buyers.length} today</span> : null}</header>
      {loading ? <div className="moderation-tool__empty"><LoaderCircle className="spin" size={17} /><span>Loading buyers</span></div> : null}
      {feed ? <><div className="buyers-list">{feed.messages.map(message => <article className="tv-message" key={message.id}><div><strong>Staff · {message.from}</strong>{message.at ? <time dateTime={new Date(message.at).toISOString()}>{messageTimestamp(message.at)}</time> : null}</div><p>{message.text}</p></article>)}{feed.buyers.map(buyer => { const selected = feed.mentioned.includes(buyer.id); return <button className={selected ? 'selected' : ''} type="button" key={buyer.id} disabled={mention.isPending} aria-pressed={selected} onClick={() => mention.mutate({ buyer, selected })}><span className="moderation-avatar">{initials(buyer.name)}</span><span><strong>{buyer.name}</strong><small>{buyer.product || 'Purchase'} · {buyer.at ? messageTimestamp(buyer.at) : 'Time unavailable'}</small></span>{selected ? <Check size={15} /> : <MonitorUp size={15} />}</button>; })}</div>{feed.buyers.length < feed.max && feed.buyers.length >= limit ? <button className="secondary-button full-width" type="button" onClick={() => onLimit(Math.min(feed.max, limit + 30))}>Load 30 more</button> : null}<form className="buyers-message" onSubmit={event => { event.preventDefault(); if (tvText.trim()) sendMessage.mutate(tvText.trim()); }}><input maxLength={200} placeholder="Message the TV screen" value={tvText} onChange={event => setTvText(event.target.value)} /><button className="icon-button" type="submit" disabled={!tvText.trim() || sendMessage.isPending} title="Send TV message" aria-label="Send TV message"><Send size={15} /></button></form>{(feed.mentioned.length || feed.messages.length) ? <button className="buyers-clear" type="button" disabled={clearing} onClick={onRequestClear}><Trash2 size={14} />Clear TV shoutouts</button> : null}</> : null}
    </section>
  );
}

function ModerationSkeletons() {
  return <div className="moderation-skeletons" role="status" aria-label="Loading live messages"><span><LoaderCircle className="spin" size={18} />Loading live messages</span>{Array.from({ length: 4 }, (_, index) => <article key={index}><i /><div><b /><span /><span /></div><em /></article>)}</div>;
}

function ConfirmDialog({ state, pending, onCancel, onConfirm }: { state: ConfirmState; pending: boolean; onCancel: () => void; onConfirm: () => void }) {
  const clearing = state.kind === 'tv';
  const resetting = state.kind === 'reset';
  const title = resetting ? 'Reset the entire chat view?' : clearing ? 'Clear every TV shoutout?' : `Delete ${state.message.sender_name}'s message?`;
  const body = resetting
    ? 'Moderation, Answered, On Air, TV Display, and AV preview will start empty for everyone. Every saved message and reply remains available in Data.'
    : clearing
      ? 'Buyer names and staff messages disappear from the TV immediately. Purchase records remain safely stored in Whop.'
      : 'It will be removed from moderation, the On Air queue, and the TV display. Attendees keep their own copy in Zoom chat.';
  const icon = resetting ? <RotateCcw size={20} /> : clearing ? <MonitorX size={20} /> : <Trash2 size={20} />;
  const confirmLabel = resetting ? 'Reset chat' : clearing ? 'Clear shoutouts' : 'Delete message';
  return <div className="moderation-confirm" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !pending) onCancel(); }}><section role="dialog" aria-modal="true" aria-labelledby="moderation-confirm-title"><span className="moderation-confirm__icon">{icon}</span><div><h2 id="moderation-confirm-title">{title}</h2><p>{body}</p></div><footer><button className="secondary-button" type="button" disabled={pending} onClick={onCancel}>{resetting ? 'Keep current chat' : 'Keep it'}</button><button className="danger-button" type="button" disabled={pending} onClick={onConfirm}>{pending ? <LoaderCircle className="spin" size={16} /> : null}{confirmLabel}</button></footer></section></div>;
}

function emptyStateFor(mode: FeedMode): { title: string; body: string } {
  if (mode === 'questions') return { title: 'No questions in this view', body: 'Messages containing a question will collect here.' };
  if (mode === 'highlighted') return { title: 'Nothing highlighted yet', body: 'Highlight a standout message and it will appear here.' };
  if (mode === 'vip') return { title: 'No VIP messages', body: 'Messages from rooms marked VIP will collect here.' };
  if (mode === 'onair') return { title: 'The On Air queue is empty', body: 'Put a message On Air to show it on the TV.' };
  if (mode === 'answered') return { title: 'Nothing answered yet', body: 'Replied and closed messages are kept here so you can reopen them.' };
  return { title: 'No messages in this view', body: 'New attendee messages will appear here the moment they arrive.' };
}

function clampToolsWidth(width: number): number {
  return Math.min(MAX_TOOLS_WIDTH, Math.max(MIN_TOOLS_WIDTH, width));
}

function readToolsWidth(): number {
  try {
    const stored = Number(window.localStorage.getItem(TOOLS_WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? clampToolsWidth(stored) : DEFAULT_TOOLS_WIDTH;
  } catch {
    return DEFAULT_TOOLS_WIDTH;
  }
}

function mergeMessages(recent: ModerationMessage[], answered: ModerationMessage[], pinnedIds: Set<number>): ModerationMessage[] {
  const merged = new Map<number, ModerationMessage>();
  for (const message of [...answered, ...recent]) merged.set(message.id, { ...message, pinned: pinnedIds.has(message.id) });
  return [...merged.values()];
}

function eventForMessage(message: ModerationMessage, byMeeting: Map<string, EventSummary>): EventSummary | undefined {
  return byMeeting.get(`${message.zoom_account}:${message.meeting_id}`)
    ?? [...byMeeting.values()].find(event => event.zoom_id === message.meeting_id);
}

function newestFirst(a: ModerationMessage, b: ModerationMessage): number {
  return b.timestamp - a.timestamp || b.id - a.id;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.charAt(0) || '?'}${parts[1]?.charAt(0) || ''}`.toUpperCase();
}

function messageTimestamp(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(timestamp);
}

function fullTimestamp(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(timestamp);
}
